import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../configs/api";
import {
  FaMapMarkerAlt,
  FaBriefcase,
  FaMoneyBillWave,
  FaClock,
  FaCalendarAlt,
} from "react-icons/fa";
import CarrierInquiryModal from "../../components/User/Common/CarrierInquiryModal";
import img from "../../Assets/Carrier/banner-img.jpeg";
import { MdTrendingUp } from "react-icons/md";
import { FaCogs, FaHandsHelping, FaAward } from "react-icons/fa";
import {
  FaShieldAlt,
  FaCheckCircle,
  FaStar,
  FaLeaf,
  FaGlobe,
  FaHandshake,
} from "react-icons/fa";
import { FaEnvelope } from "react-icons/fa";
import team from "../../Assets/Carrier/team-img.webp";

// Skeleton Card
const CarrierCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
    <div className="w-full h-52 bg-gray-200" />
    <div className="p-5 space-y-3">
      <div className="h-3 bg-gray-200 rounded w-1/3" />
      <div className="h-5 bg-gray-200 rounded w-full" />
      <div className="h-5 bg-gray-200 rounded w-4/5" />
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-3 bg-gray-200 rounded w-3/4" />
      <div className="h-8 bg-gray-200 rounded-lg w-28 mt-2" />
    </div>
  </div>
);

const Carrier = () => {
  const navigate = useNavigate();
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedCarrierId, setSelectedCarrierId] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchCarriers();
  }, []);

  const fetchCarriers = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/carriers/");

      const data = res.data?.data?.carriers || [];

      setCarriers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching carriers:", err);
      setError("Failed to load carriers.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const stripHtml = (html) => html?.replace(/<[^>]*>/g, "") || "";

  // Filter
  const filtered = carriers.filter(
    (b) =>
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      stripHtml(b.description).toLowerCase().includes(search.toLowerCase()),
  );

  const data = [
    {
      icon: <MdTrendingUp />,
      heading: "GROWTH",
      description:
        "Learn, grow and advance your career with real opportunities.",
    },
    {
      icon: <FaCogs />,
      heading: "DYNAMIC ENVIRONMENT",
      description:
        "Work in a fast-paced, challenging and rewarding environment.",
    },
    {
      icon: <FaHandsHelping />,
      heading: "TEAM SPIRIT",
      description:
        "Collaborate with passionate teams that value respect and teamwork.",
    },
    {
      icon: <FaShieldAlt />,
      heading: "STABILITY",
      description:
        "Be part of a growing organization with a strong foundation.",
    },
    {
      icon: <FaAward />,
      heading: "RECOGNITION",
      description: "Your performance is recognized and rewarded.",
    },
  ];

  const items = [
    {
      icon: <FaGlobe />,
      title: "STRONGER PROJECTS",
      desc: "Driven by quality and commitment",
    },
    {
      icon: <FaShieldAlt />,
      title: "STRONGER PEOPLE",
      desc: "Empowered, trained and motivated",
    },
    {
      icon: <FaLeaf />,
      title: "SUSTAINABLE FUTURE",
      desc: "Building responsibly for tomorrow",
    },
    {
      icon: <FaHandshake />,
      title: "TOGETHER, WE BUILD MORE",
      desc: "Join us in shaping a better tomorrow",
    },
  ];

  return (
    <>
      <div>
        <img src={img} alt="" className="w-full object-cover" />
      </div>
      <div className="w-full bg-white px-4 md:px-8 lg:px-10 py-10">
        {/* Header */}
        <div className="text-center md:mb-14">
          <span
            className="text-xs font-medium tracking-widest uppercase"
            style={{ color: "#c020d0", letterSpacing: "0.2em" }}
          >
            [OUR CARRIERS]
          </span>

          <h1 className="text-3xl md:text-4xl lg:text-5xl text-[#041122] font-heading font-semibold mt-4">
            Why Join Mix Masters?
          </h1>
        </div>

        {/* Features Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-10">
          {data.map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center text-center px-4 py-6 border-r last:border-r-0"
            >
              {/* Icon */}
              <div className="text-5xl mb-4 text-btnColor">{item.icon}</div>

              {/* Heading */}
              <h3 className="text-sm font-bold tracking-wide text-btnColor">
                {item.heading}
              </h3>

              {/* Description */}
              <p className="text-xs text-gray-800 mt-2 tracking-wide leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* Error */}
        {error && !loading && (
          <div className="flex flex-col items-center py-20 text-center">
            <span className="text-5xl mb-4">😕</span>
            <p className="text-gray-500">{error}</p>
            <button
              onClick={fetchCarriers}
              className="mt-4 px-6 py-2 bg-btnColor text-white rounded-xl text-sm hover:bg-white hover:text-btnColor border border-btnColor transition"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center py-20 text-center">
            <span className="text-5xl mb-4">📝</span>
            <p className="text-gray-600 font-medium text-lg">
              No carriers found
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {search
                ? "Try a different search term"
                : "No carriers published yet"}
            </p>
            {search && (
              <button
                onClick={() => setSearch("")}
                className="mt-3 px-5 py-1.5 bg-btnColor text-white rounded-lg text-xs"
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <CarrierCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {carriers.map((item) => (
              <div
                key={item._id}
                className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 
      hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group"
              >
                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-pink-600 transition">
                  {item.name}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-500 mt-2 line-clamp-1">
                  {stripHtml(item.description)}
                </p>

                {/* Info Section */}
                <div className="mt-4 space-y-3 text-sm text-gray-600">
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <FaMapMarkerAlt /> Location
                    </span>
                    <span>{item.location}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <FaBriefcase /> Experience
                    </span>
                    <span>{item.experience}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <FaMoneyBillWave /> Salary
                    </span>
                    <span className="text-green-600 font-semibold">
                      {item.salary}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <FaClock /> Time
                    </span>
                    <span>{item.time}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <FaCalendarAlt /> Date
                    </span>
                    <span>
                      {item?.date
                        ? new Date(item.date).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "N/A"}
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t my-4"></div>

                {/* Button */}
                <button
                  onClick={() => {
                    setSelectedCarrierId(item._id);
                    setOpenModal(true);
                  }}
                  className="block  w-1/2 mx-auto bg-btnColor hover:bg-btnHoverColor text-white font-medium py-3 rounded-xl transition duration-300"
                >
                  Apply Now
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
          <div>
            <div className="p-5 rounded-lg bg-gray-100 flex flex-col gap-4">
              <p className="text-lg text-black font-bold tracking-wide">
                OUR CULTURE
              </p>
              <span className="h-1 w-10 bg-btnColor"></span>
              <p className="text-sm tracking-wide text-gray-800">
                We are builders, problem solvers and innovators.Our culture is
                built on trust, accountability and a relentless drive to deliver
                the best.
              </p>

              <ul className="space-y-1 text-xs font-semibold">
                <li className="flex items-center gap-2">
                  <FaShieldAlt className="text-white px-1 py-1 bg-btnColor text-2xl rounded-full" />
                  Integrity in everything we do
                </li>
                <li className="flex items-center gap-2">
                  <FaCheckCircle className="text-white px-1 py-1 bg-btnColor text-2xl rounded-full" />
                  Commitment to quality
                </li>
                <li className="flex items-center gap-2">
                  <FaStar className="text-white px-1 py-1 bg-btnColor text-2xl rounded-full" />
                  passion for excellence
                </li>
                <li className="flex items-center gap-2">
                  <FaLeaf className="text-white px-1 py-1 bg-btnColor text-2xl rounded-full" />
                  Care for people and the planet
                </li>
              </ul>
            </div>
          </div>
          <div className="relative w-full">
            {/* Image */}
            <img
              src={team}
              alt=""
              className="w-full object-cover h-[287px] rounded-lg"
            />

            {/* Text Overlay */}
            <div className="absolute bottom-0 left-0 w-full bg-btnColor bg-opacity-80 text-white text-semibold  text-center py-3 px-4 rounded-b-lg">
              Behind every strong structure, there is a stronger team.
            </div>
          </div>
          <div>
            <div className="p-5 rounded-lg bg-pink-100 flex flex-col gap-4">
              <p className="text-lg text-btnColor font-bold tracking-wide">
                READY TO BUILD YOUR FUTURE?
              </p>

              <p className="text-sm tracking-wide text-gray-800">
                Take the next step in your career with us. Join a team where
                your skills, ideas, and ambition shape real-world projects and
                meaningful impact. We provide the platform, you build the
                future.
              </p>

              <div className="flex items-center gap-2 text-sm ">
                <span>
                  Send your resume to{" "}
                  <span className="flex gap-3 mt-3 font-semibold text-gray-900">
                    <FaEnvelope className="text-btnColor size-5" />
                    careers@mixmasters.in
                  </span>
                </span>
              </div>
              <p className="text-sm tracking-wide text-gray-800">
                Explore the available roles below and apply for the position
                that best matches your interests and skills.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full bg-btnColor text-white py-4 px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {items.map((item, index) => (
            <div key={index} className="flex items-center gap-4">
              {/* Icon */}
              <div className="text-2xl">{item.icon}</div>

              {/* Text */}
              <div>
                <h3 className="text-sm font-semibold tracking-wide">
                  {item.title}
                </h3>
                <p className="text-xs opacity-90">{item.desc}</p>
              </div>

              {/* Divider */}
              {index !== items.length - 1 && (
                <div className="hidden md:block h-10 w-px bg-white/40 ml-10"></div>
              )}
            </div>
          ))}
        </div>
      </div>
      <CarrierInquiryModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        carrierId={selectedCarrierId}
      />
    </>
  );
};

export default Carrier;
